// Round-trip import of the System Breakdown Structure from CSV / XLSX.
//
// Spreadsheet shape (header row required, case-insensitive):
//   System | Sub-System | Sub-Subsystem | General | Repairable
//
// Row semantics:
//   - "System" filled, the other two blank      → adds a System-level node
//   - "System" + "Sub-System" filled            → adds a Sub-System under that System
//   - all three filled                          → adds a Sub-Subsystem
// Systems / Sub-Systems are de-duplicated by name on import (case-insensitive).
// Quantity columns default to 0 when blank.

import * as XLSX from "xlsx";
import {
  buildEmptySystem,
  buildEmptySubsystem,
  buildEmptySubsubsystem,
} from "./s1000dData";

function norm(s) { return String(s ?? "").trim(); }
function lc(s) { return norm(s).toLowerCase(); }
function toInt(v) {
  const n = parseInt(norm(v), 10);
  return Number.isFinite(n) ? n : 0;
}

async function readRows(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  return XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, blankrows: false });
}

const SYSTEM_KEYS = ["system", "systems"];
const SUB_KEYS = ["sub-system", "subsystem", "sub system", "sub_system"];
const SUBSUB_KEYS = ["sub-subsystem", "subsubsystem", "sub-sub-system", "sub sub system", "sub_subsystem"];
const GEN_KEYS = ["general", "general qty", "general items"];
const REP_KEYS = ["repairable", "repairable qty", "repairable items"];

function buildHeaderMap(headerRow) {
  const map = {};
  (headerRow || []).forEach((cell, idx) => {
    const key = lc(cell);
    if (!key) return;
    if (SYSTEM_KEYS.includes(key)) map.system = idx;
    else if (SUB_KEYS.includes(key)) map.sub = idx;
    else if (SUBSUB_KEYS.includes(key)) map.subsub = idx;
    else if (GEN_KEYS.includes(key)) map.general = idx;
    else if (REP_KEYS.includes(key)) map.repairable = idx;
  });
  return map;
}

export async function parseSbsImport(file) {
  if (!file) throw new Error("No file selected");
  const ext = (file.name || "").toLowerCase().split(".").pop();
  if (!["csv", "xlsx", "xls"].includes(ext)) {
    throw new Error(`Unsupported file type: .${ext}. Use .csv or .xlsx`);
  }
  let rows;
  try { rows = await readRows(file); }
  catch (err) { throw new Error(`Failed to parse file: ${err.message || err}`); }
  if (!rows || rows.length === 0) throw new Error("File is empty");

  // Skip leading blank rows / single-cell titles
  let headerIdx = -1;
  for (let i = 0; i < rows.length; i++) {
    const map = buildHeaderMap(rows[i]);
    if (map.system !== undefined) { headerIdx = i; break; }
  }
  if (headerIdx === -1) {
    throw new Error("No 'System' column header found. Expected columns: System, Sub-System, Sub-Subsystem, General, Repairable.");
  }
  const hmap = buildHeaderMap(rows[headerIdx]);

  const systems = [];
  const sysByName = new Map(); // lc(name) → system node
  const warnings = [];

  // Header completeness check — flag missing optional columns up front so the
  // user understands why some rows might land at the wrong level.
  const expected = { system: "System", sub: "Sub-System", subsub: "Sub-Subsystem", general: "General", repairable: "Repairable" };
  const missingCols = Object.entries(expected)
    .filter(([key]) => hmap[key] === undefined)
    .map(([, label]) => label);
  if (missingCols.length > 0) {
    warnings.push(`Header is missing optional column(s): ${missingCols.join(", ")}. Rows that need them will be treated as 0 / parent-level.`);
  }

  let rowsConsidered = 0;
  let rowsApplied = 0;
  let rowsZeroQty = 0;
  let dupSystems = 0;
  let dupSubs = 0;
  let dupSubsubs = 0;

  for (let i = headerIdx + 1; i < rows.length; i++) {
    const r = rows[i] || [];
    const sysName = norm(r[hmap.system]);
    const subName = hmap.sub !== undefined ? norm(r[hmap.sub]) : "";
    const subSubName = hmap.subsub !== undefined ? norm(r[hmap.subsub]) : "";
    const gen = hmap.general !== undefined ? toInt(r[hmap.general]) : 0;
    const rep = hmap.repairable !== undefined ? toInt(r[hmap.repairable]) : 0;
    if (!sysName && !subName && !subSubName) continue;
    rowsConsidered++;
    if (!sysName) {
      warnings.push(`Row ${i + 1}: missing System name — skipped.`);
      continue;
    }
    if (gen === 0 && rep === 0) {
      rowsZeroQty++;
    }
    let sysNode = sysByName.get(lc(sysName));
    if (!sysNode) {
      sysNode = { ...buildEmptySystem(), name: sysName };
      systems.push(sysNode);
      sysByName.set(lc(sysName), sysNode);
    } else if (!subName && !subSubName) {
      dupSystems++;
    }
    if (!subName && !subSubName) {
      sysNode.generalQty += gen;
      sysNode.repairableQty += rep;
      rowsApplied++;
      continue;
    }
    let subNode = (sysNode.subsystems || []).find((s) => lc(s.name) === lc(subName));
    if (!subNode) {
      if (!subName) {
        warnings.push(`Row ${i + 1}: Sub-Subsystem given without a Sub-System — skipped.`);
        continue;
      }
      subNode = { ...buildEmptySubsystem(), name: subName };
      sysNode.subsystems = [...(sysNode.subsystems || []), subNode];
    } else if (!subSubName) {
      dupSubs++;
    }
    if (!subSubName) {
      subNode.generalQty += gen;
      subNode.repairableQty += rep;
      rowsApplied++;
      continue;
    }
    let ssNode = (subNode.subsubsystems || []).find((s) => lc(s.name) === lc(subSubName));
    if (!ssNode) {
      ssNode = { ...buildEmptySubsubsystem(), name: subSubName };
      subNode.subsubsystems = [...(subNode.subsubsystems || []), ssNode];
    } else {
      dupSubsubs++;
    }
    ssNode.generalQty += gen;
    ssNode.repairableQty += rep;
    rowsApplied++;
  }

  if (dupSystems > 0) warnings.push(`${dupSystems} row(s) targeted a System that already existed — quantities accumulated.`);
  if (dupSubs > 0) warnings.push(`${dupSubs} row(s) targeted a Sub-System that already existed — quantities accumulated.`);
  if (dupSubsubs > 0) warnings.push(`${dupSubsubs} row(s) targeted a Sub-Subsystem that already existed — quantities accumulated.`);
  if (rowsZeroQty > 0) warnings.push(`${rowsZeroQty} row(s) had both General and Repairable as 0 — added to the tree but contribute no DMs.`);

  return {
    systems,
    warnings,
    count: systems.length,
    stats: {
      rowsConsidered,
      rowsApplied,
      rowsSkipped: rowsConsidered - rowsApplied,
      rowsZeroQty,
      duplicates: { systems: dupSystems, subsystems: dupSubs, subsubsystems: dupSubsubs },
      systemCount: systems.length,
    },
  };
}

export function buildSbsTemplateWorkbook() {
  const rows = [
    ["S1000D System Breakdown Structure — Import Template"],
    [],
    ["System", "Sub-System", "Sub-Subsystem", "General", "Repairable"],
    ["Hydraulics", "", "", 1, 0],
    ["Hydraulics", "Pumps", "", 2, 1],
    ["Hydraulics", "Pumps", "Pressure Sensor", 0, 3],
    ["Hydraulics", "Reservoir", "Filter", 0, 2],
    ["Avionics", "Flight Computer", "", 1, 1],
    ["Avionics", "Flight Computer", "I/O Card", 0, 2],
  ];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "SBS");
  return wb;
}

export function downloadSbsTemplate() {
  XLSX.writeFile(buildSbsTemplateWorkbook(), "s1000d_sbs_template.xlsx");
}

// S1000D Bike sample data — modelled after the publicly-known Bike data set
// from the S1000D issue 4.0+ training reference.  Five top-level systems
// (Frame, Wheels, Brakes, Drivetrain, Steering) decomposed into sub-systems
// and sub-subsystems with realistic General/Repairable mix.
export function buildBikeSampleSbsWorkbook() {
  const rows = [
    ["S1000D Bike — Sample System Breakdown Structure"],
    ["Source: well-known S1000D Bike training data set, populated for testing the SBS importer."],
    [],
    ["System", "Sub-System", "Sub-Subsystem", "General", "Repairable"],
    ["Frame", "", "", 2, 0],
    ["Frame", "Frame Tubes", "", 1, 0],
    ["Frame", "Seat Post", "", 1, 1],
    ["Wheels", "", "", 1, 0],
    ["Wheels", "Front Wheel", "", 1, 0],
    ["Wheels", "Front Wheel", "Hub", 1, 1],
    ["Wheels", "Front Wheel", "Rim", 1, 0],
    ["Wheels", "Front Wheel", "Spokes", 1, 1],
    ["Wheels", "Rear Wheel", "", 1, 0],
    ["Wheels", "Rear Wheel", "Hub", 1, 1],
    ["Wheels", "Rear Wheel", "Rim", 1, 0],
    ["Wheels", "Rear Wheel", "Spokes", 1, 1],
    ["Brakes", "", "", 1, 0],
    ["Brakes", "Front Brake", "", 1, 0],
    ["Brakes", "Front Brake", "Caliper", 1, 1],
    ["Brakes", "Front Brake", "Pads", 1, 1],
    ["Brakes", "Front Brake", "Lever", 1, 1],
    ["Brakes", "Rear Brake", "", 1, 0],
    ["Brakes", "Rear Brake", "Caliper", 1, 1],
    ["Brakes", "Rear Brake", "Pads", 1, 1],
    ["Brakes", "Rear Brake", "Lever", 1, 1],
    ["Drivetrain", "", "", 1, 0],
    ["Drivetrain", "Chain", "", 1, 1],
    ["Drivetrain", "Crankset", "", 1, 0],
    ["Drivetrain", "Crankset", "Chainring", 1, 1],
    ["Drivetrain", "Crankset", "Crank Arm", 1, 1],
    ["Drivetrain", "Crankset", "Pedals", 2, 2],
    ["Drivetrain", "Cassette", "", 1, 1],
    ["Drivetrain", "Derailleur", "", 1, 0],
    ["Drivetrain", "Derailleur", "Front", 1, 1],
    ["Drivetrain", "Derailleur", "Rear", 1, 1],
    ["Steering", "", "", 1, 0],
    ["Steering", "Handlebars", "", 1, 0],
    ["Steering", "Stem", "", 1, 0],
    ["Steering", "Headset", "", 1, 1],
    ["Steering", "Saddle", "", 1, 0],
  ];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Bike SBS");
  return wb;
}

export function downloadBikeSampleSbs() {
  XLSX.writeFile(buildBikeSampleSbsWorkbook(), "s1000d_bike_sample_sbs.xlsx");
}
